import Vue from 'vue';
import { Site, Settings } from '@/interfaces/multiTenancy';
import { Commit } from 'vuex';

interface SiteState {
	sites: {
		[id: number]: Site;
	};
	selectedSite: Site | null;
}

interface SiteEdit {
	name: string;
	fk_client?: number | null;
	description: string;
	settings: Settings;
	tzname: string;
	css: string;
	url: string;
}

export default {
	namespaced: true,
	state: {
		sites: [],
		selectedSite: null,
	} as SiteState,
	getters: {
		getSites: (state: SiteState): Site[] => {
			return Object.values(state.sites);
		},

		getSite:
			(state: SiteState) =>
			(id: number): Site | undefined => {
				return state.sites[id];
			},

		getSelectedSite: (state: SiteState): Site | null => {
			return state.selectedSite;
		},

		getClientSites:
			(state: SiteState) =>
			(clientId: number): Site[] => {
				return Object.values(state.sites).filter((site: Site) => site.fk_client === clientId);
			},
	},
	mutations: {
		SET_SITES(state: SiteState, sites: Site[]): void {
			const ret: { [id: number]: Site } = {};
			state.sites = sites.reduce((obj, site) => {
				obj[site.id] = site;
				return obj;
			}, ret);
		},

		ADD_SITE(state: SiteState, site: Site): void {
			Vue.set(state.sites, site.id, site);
		},

		CHANGE_SITE(state: SiteState, site: Site & { id: number }): void {
			const oldSite = state.sites[site.id] || {};
			Vue.set(state.sites, site.id, { ...oldSite, ...site });
		},

		CHANGE_SELECTED_SITE(state: SiteState, site: Site): void {
			state.selectedSite = site;
		},

		CLEAN_SITE(state: SiteState): void {
			state.sites = {};
		},

		CLEAN_SELECTED_SITE(state: SiteState): void {
			state.selectedSite = null;
		},
	},
	actions: {
		async fetchSites({ commit }: { commit: Commit }): Promise<void> {
			const response = await Vue.axios.get(`${process.env.VUE_APP_API_URL}/sites`, {
				headers: {
					Authorization: Vue.$cookies.get('Global-Token').token,
				},
			});

			if (response && response.status === 200) {
				commit('SET_SITES', response.data.data);
			}
		},

		async addSite({ commit }: { commit: Commit }, { site }: { site: SiteEdit }): Promise<void> {
			const response = await Vue.axios.post(`${process.env.VUE_APP_API_URL}/sites`, site);

			if (response && response.status === 200) {
				commit('ADD_SITE', response.data.data);
			}
		},

		async modifySite(
			{ commit }: { commit: Commit },
			{ id, site }: { id: number; site: SiteEdit }
		): Promise<void> {
			const response = await Vue.axios.put(`${process.env.VUE_APP_API_URL}/sites/${id}`, site);

			if (response && response.status === 200) {
				commit('CHANGE_SITE', response.data.data);
			}
		},

		selectSite({ commit }: { commit: Commit }, { site }: { site: Site }): void {
			if (site) {
				commit('CHANGE_SELECTED_SITE', site);
			}
		},

		cleanSite({ commit }: { commit: Commit }): void {
			commit('CLEAN_SITE');
		},

		cleanSelectedSite({ commit }: { commit: Commit }): void {
			commit('CLEAN_SELECTED_SITE');
		},

		cleanAllSite({ commit }: { commit: Commit }): void {
			commit('CLEAN_SITE');
			commit('CLEAN_SELECTED_SITE');
		},
	},
};
